// Utility to handle pending score saves when network is unavailable
const PENDING_SAVES_KEY = "pendingScoreSaves";

/**
 * Store a pending save operation locally
 */
export function storePendingSave(saveData) {
  try {
    const pending = getPendingSaves();
    // Avoid duplicates - check if similar save was added recently (within last 5 seconds)
    const recentTimestamp = Date.now() - 5000;
    const exists = pending.some(
      (p) =>
        p.score === saveData.score &&
        p.streak === saveData.streak &&
        p.numbersPlayed === saveData.numbersPlayed &&
        p.maxStreak === saveData.maxStreak &&
        p.timestamp > recentTimestamp
    );
    
    if (!exists) {
      pending.push({
        ...saveData,
        timestamp: saveData.timestamp || Date.now(),
        retryCount: saveData.retryCount || 0,
      });
      localStorage.setItem(PENDING_SAVES_KEY, JSON.stringify(pending));
      console.log("Stored pending save for retry:", saveData);
    }
  } catch (error) {
    console.error("Failed to store pending save:", error);
  }
}

/**
 * Get all pending saves from localStorage
 */
export function getPendingSaves() {
  try {
    const stored = localStorage.getItem(PENDING_SAVES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error("Failed to get pending saves:", error);
    return [];
  }
}

/**
 * Remove a pending save (after successful save)
 */
export function removePendingSave(index) {
  try {
    const pending = getPendingSaves();
    pending.splice(index, 1);
    localStorage.setItem(PENDING_SAVES_KEY, JSON.stringify(pending));
  } catch (error) {
    console.error("Failed to remove pending save:", error);
  }
}

/**
 * Clear all pending saves
 */
export function clearPendingSaves() {
  try {
    localStorage.removeItem(PENDING_SAVES_KEY);
  } catch (error) {
    console.error("Failed to clear pending saves:", error);
  }
}

/**
 * Check if we're online
 */
export function isOnline() {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
}

/**
 * Listen for online/offline events
 */
export function setupNetworkListener(onOnline) {
  if (typeof window === "undefined") return () => {};

  const handleOnline = () => {
    console.log("Network connection restored");
    onOnline?.();
  };

  window.addEventListener("online", handleOnline);

  // Return cleanup function
  return () => {
    window.removeEventListener("online", handleOnline);
  };
}

/**
 * Retry all pending saves
 * Returns number of successfully saved items
 */
export async function retryPendingSaves(saveUserScoresFn) {
  if (!isOnline()) {
    console.log("Still offline, cannot retry pending saves");
    return 0;
  }

  const pending = getPendingSaves();
  if (pending.length === 0) {
    return 0;
  }

  console.log(`Retrying ${pending.length} pending save(s)...`);

  let successCount = 0;
  const toRemove = [];
  const updatedPending = [];

  for (let i = 0; i < pending.length; i++) {
    const saveData = pending[i];
    
    // Skip if too old (older than 7 days)
    const age = Date.now() - saveData.timestamp;
    const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days
    if (age > maxAge) {
      console.log(`Skipping pending save (too old):`, saveData);
      toRemove.push(i);
      continue;
    }

    try {
      await saveUserScoresFn({
        score: saveData.score,
        streak: saveData.streak,
        maxStreak: saveData.maxStreak,
        numbersPlayed: saveData.numbersPlayed,
        monthlyIncrement: saveData.monthlyIncrement,
        lastWonId: saveData.lastWonId,
        lastScore: saveData.lastScore,
        lastPlayedId: saveData.lastPlayedId,
        incrementPlayedCount: saveData.incrementPlayedCount,
      });
      
      // Success - mark for removal
      toRemove.push(i);
      successCount++;
      console.log(`Successfully retried pending save`);
    } catch (error) {
      console.warn(`Failed to retry pending save, will try again later:`, error);
      // Increment retry count
      saveData.retryCount = (saveData.retryCount || 0) + 1;
      updatedPending.push({ index: i, data: saveData });
      
      // If too many retries, give up (remove it)
      if (saveData.retryCount > 10) {
        console.warn(`Giving up on pending save after ${saveData.retryCount} retries`);
        toRemove.push(i);
      }
    }
  }

  // Remove successfully saved or expired items (in reverse order to maintain indices)
  for (let i = toRemove.length - 1; i >= 0; i--) {
    removePendingSave(toRemove[i]);
  }

  // Update remaining items with incremented retry counts
  if (updatedPending.length > 0) {
    const allPending = getPendingSaves();
    updatedPending.forEach(({ index, data }) => {
      if (!toRemove.includes(index) && allPending[index]) {
        allPending[index] = data;
      }
    });
    localStorage.setItem(PENDING_SAVES_KEY, JSON.stringify(allPending));
  }

  return successCount;
}
